// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/RecebimentoImportar.tsx
//
//  Iteração 4 (Fase 2): upload de XML NF-e + matching contra banco
//  + confirmação transacional (CompraRecebimento + estoque + ContaPagar +
//  ProdutoFornecedor + histórico de preço de compra).
// ════════════════════════════════════════════════════════════════════════════

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import {
  FileUp, FileText, AlertCircle, AlertTriangle, CheckCircle2, Cloud,
  Link2, UserCheck, UserX, Package, PackageX, PackageCheck, Check, X,
} from 'lucide-react';

import { usePreviewRecebimento, useConfirmarRecebimento, useBaixarXmlSefaz }
  from '@/features/compras/hooks/useRecebimento';
import { OrdemCompraSearchCombobox } from '@/components/OrdemCompraSearchCombobox';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import type {
  RecebimentoPreview, Validacao, ItemPreview, PreviewResumo,
  ConfirmarRecebimentoRequest,
} from '@/features/compras/types';
import { getApiErrorMessage } from '@/lib/apiClient';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const fileParaBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // remove o prefixo "data:application/xml;base64,"
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// ── Resoluções do usuário por item do XML ─────────────────────────────────────

/** O que o usuário ajusta em cada item antes de confirmar (chave = nItem do XML). */
interface AjusteItem {
  /** Produto escolhido à mão (vínculo manual ou troca do match automático). */
  produtoId?: number;
  /** Unidades de estoque por unidade do XML; vazio = sugestão do fornecedor ou 1. */
  fator?: string;
  lote?: string;
  validade?: string;
}
type Ajustes = Record<number, AjusteItem>;

const produtoDoItem = (it: ItemPreview, ajustes: Ajustes) =>
  ajustes[it.xml.numeroItem]?.produtoId ?? it.produto.produtoId;

const fatorDoItem = (it: ItemPreview, ajustes: Ajustes) => {
  const digitado = Number((ajustes[it.xml.numeroItem]?.fator ?? '').replace(',', '.'));
  return digitado > 0 ? digitado : it.produto.fatorConversaoEntrada ?? 1;
};

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtMoeda = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtQtd = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const fmtData = (s?: string) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('pt-BR');
  } catch {
    return s;
  }
};

const fmtCnpj = (c?: string) => {
  if (!c) return '—';
  const d = c.replace(/\D/g, '');
  if (d.length === 14)
    return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11)
    return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return c;
};

// ── componente ───────────────────────────────────────────────────────────────

const RecebimentoImportar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [cnpjDestinatario, setCnpjDestinatario] = useState('');
  const [poId, setPoId] = useState<number | null>(null);
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const [aba, setAba] = useState<string>('itens');

  // Config da confirmação
  const [localEstoqueId, setLocalEstoqueId] = useState<string>('');
  const [observacao,     setObservacao]     = useState<string>('');
  const [prazoAvulso,    setPrazoAvulso]    = useState<string>('30');
  const [ajustes,        setAjustes]        = useState<Ajustes>({});
  const ajustarItem = (numeroItem: number, parcial: AjusteItem) =>
    setAjustes((a) => ({ ...a, [numeroItem]: { ...a[numeroItem], ...parcial } }));

  const { preview: runPreview, isPreviewing, data, erro, reset } = usePreviewRecebimento();
  const { confirmar, isConfirming } = useConfirmarRecebimento();
  const { baixar: baixarSefaz, isBaixando: isBaixandoSefaz } = useBaixarXmlSefaz();

  const [chaveParaBaixar, setChaveParaBaixar] = useState('');

  // Combos
  const { data: locais = [] } = useLocalEstoqueCombo();

  const handleArquivo = async (file: File) => {
    setXmlFile(file);
    setAjustes({});
    try {
      await runPreview({
        arquivo:          file,
        cnpjDestinatario: cnpjDestinatario.replace(/\D/g, '') || undefined,
        compraOrdemId:    poId ?? undefined,
      });
    } catch {
      // erro via hook
    }
  };

  const erros  = data?.staging.validacoes.filter((v) => v.severidade === 'Erro')  ?? [];
  const avisos = data?.staging.validacoes.filter((v) => v.severidade === 'Aviso') ?? [];

  const handleLimpar = () => {
    reset();
    setXmlFile(null);
    setAjustes({});
    setLocalEstoqueId('');
    setObservacao('');
    setChaveParaBaixar('');
  };

  const handleBaixarXmlSefaz = async () => {
    const chave = chaveParaBaixar.replace(/\D/g, '');
    if (chave.length !== 44) {
      toast.error('Informe uma chave NF-e válida (44 dígitos).');
      return;
    }
    try {
      const res = await baixarSefaz(chave);
      if (res.sucesso && res.xmlBase64) {
        // Converte base64 → File e dispara preview
        const bin = atob(res.xmlBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const file = new File([bytes], `${chave}.xml`, { type: 'application/xml' });
        await handleArquivo(file);
      } else {
        toast.message(`cStat ${res.cStat}`, { description: res.xMotivo });
      }
    } catch (err) {
      toast.error((err as Error).message ?? 'Falha ao consultar SEFAZ');
    }
  };

  const handleConfirmar = async () => {
    if (!data || !xmlFile) return;
    if (data.fornecedor.fornecedorId == null || data.fornecedor.fornecedorId <= 0) {
      toast.error('Fornecedor não resolvido. Cadastre a Pessoa antes de confirmar.');
      return;
    }
    if (!localEstoqueId) {
      toast.error('Selecione o local de estoque de destino.');
      return;
    }
    const semProduto = data.itens.filter(i => !(Number(produtoDoItem(i, ajustes)) > 0));
    if (semProduto.length > 0) {
      toast.error(
        `Vincule um produto aos itens ${semProduto.map(i => `#${i.xml.numeroItem}`).join(', ')} na aba Itens antes de confirmar.`);
      setAba('itens');
      return;
    }

    const base64 = await fileParaBase64(xmlFile);
    const localDefault = Number(localEstoqueId);

    const request: ConfirmarRecebimentoRequest = {
      base64Xml:        base64,
      compraOrdemId:    data.ordemVinculada?.id,
      fornecedorId:     data.fornecedor.fornecedorId,
      localEstoqueId:   localDefault,
      usuarioId:        user?.id ?? 0,
      centroCustoId:    1,
      contaContabilId:  1,
      tipoDespesasId:   1,
      grupoDespesasId:  1,
      observacao:       observacao || undefined,
      prazoPagamentoDiasSeAvulso:
        data.staging.parcelas.length === 0 ? Number(prazoAvulso) || 30 : undefined,
      itens: data.itens.map(i => ({
        numeroItem:          i.xml.numeroItem,
        produtoId:           produtoDoItem(i, ajustes)!,
        compraOrdemItemId:   i.poItem?.compraOrdemItemId,
        quantidadeRecebida:  i.xml.quantidade,
        localEstoqueId:      localDefault,
        fatorConversao:      fatorDoItem(i, ajustes),
        lote:                ajustes[i.xml.numeroItem]?.lote?.trim() || undefined,
        dtValidade:          ajustes[i.xml.numeroItem]?.validade || undefined,
      })),
      parcelas: data.staging.parcelas.length > 0
        ? data.staging.parcelas.map(p => ({
            numero:         p.numero,
            dataVencimento: p.dataVencimento,
            valor:          p.valor,
          }))
        : undefined,
    };

    try {
      const res = await confirmar(request);
      toast.success(
        `Recebimento nº ${res.numero} criado · conta a pagar gerada · ${res.itensCriados} itens.`,
      );
      handleLimpar();
      // Redireciona para o Ajuste de Preço (Iteração 5).
      navigate(`/compras/recebimentos/${res.id}/ajuste-preco`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Importar NF-e (Recebimento)</h1>
          <p className="text-muted-foreground mt-1">
            Upload do XML → matching com cadastro → confirmação transacional
            (cria recebimento, movimenta estoque, gera ContaPagar e atualiza custos).
          </p>
        </div>
      </div>

      {/* Upload + opções */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5 text-primary" />
            Upload do XML
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label>Vincular a Pedido de Compra (opcional)</Label>
              <OrdemCompraSearchCombobox
                value={poId}
                onChange={setPoId}
                placeholder="— recebimento avulso —"
              />
              <p className="text-xs text-muted-foreground">
                Quando vinculado, habilita three-way match (PO × XML × recebimento).
              </p>
            </div>

            <div className="space-y-1">
              <Label>CNPJ do destinatário esperado (opcional)</Label>
              <Input
                value={cnpjDestinatario}
                onChange={(e) => setCnpjDestinatario(e.target.value)}
                placeholder="00.000.000/0000-00"
              />
              <p className="text-xs text-muted-foreground">
                Valida que a nota é destinada a esta empresa.
              </p>
            </div>

            <div className="flex items-end gap-2">
              <input
                ref={inputRef}
                type="file"
                accept=".xml"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleArquivo(f);
                  e.target.value = '';
                }}
              />
              <Button
                onClick={() => inputRef.current?.click()}
                disabled={isPreviewing}
                className="gap-2"
              >
                <FileUp className="h-4 w-4" />
                {isPreviewing ? 'Processando…' : 'Selecionar XML'}
              </Button>
              {data && (
                <Button variant="ghost" onClick={handleLimpar}>Limpar</Button>
              )}
            </div>
          </div>

          {erro && (
            <div className="p-3 rounded-md border border-destructive/50 bg-destructive/10 text-destructive text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>
                Falha ao importar: {(erro as Error).message ?? 'erro desconhecido'}
              </span>
            </div>
          )}

          {/* Baixar direto da SEFAZ (stub — dívida técnica Fase 3) */}
          <div className="pt-2 border-t">
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <Cloud className="h-4 w-4" />
                Baixar XML diretamente da SEFAZ (por chave de acesso)
              </summary>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Chave NF-e (44 dígitos)</Label>
                  <Input
                    value={chaveParaBaixar}
                    onChange={(e) => setChaveParaBaixar(e.target.value)}
                    placeholder="00000000000000000000000000000000000000000000"
                    className="font-mono text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={handleBaixarXmlSefaz}
                    disabled={isBaixandoSefaz || isPreviewing}
                    className="gap-2"
                  >
                    <Cloud className="h-4 w-4" />
                    {isBaixandoSefaz ? 'Consultando…' : 'Baixar da SEFAZ'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                ⚠️ Integração ACBr pendente. Hoje retorna <code>cStat=999</code> —
                use o upload manual do XML até que o envio esteja configurado.
              </p>
            </details>
          </div>
        </CardContent>
      </Card>

      {/* Resultado */}
      {data && (
        <>
          {/* Status + resumo da confirmação */}
          <StatusCard
            preview={data}
            onConfirmar={() => setAba('confirmar')}
          />

          {/* Cards de cabeçalho (nota + fornecedor + PO + valor) */}
          <ResumoCard preview={data} />

          {/* Validações */}
          {(erros.length > 0 || avisos.length > 0) && (
            <ValidacoesCard erros={erros} avisos={avisos} />
          )}

          {/* Abas */}
          <Card>
            <CardContent className="pt-6">
              <Tabs value={aba} onValueChange={setAba}>
                <TabsList>
                  <TabsTrigger value="itens">Itens ({data.itens.length})</TabsTrigger>
                  <TabsTrigger value="cabecalho">Cabeçalho</TabsTrigger>
                  <TabsTrigger value="impostos">Impostos</TabsTrigger>
                  <TabsTrigger value="financeiro">Financeiro ({data.staging.parcelas.length})</TabsTrigger>
                  <TabsTrigger value="confirmar">Confirmar</TabsTrigger>
                </TabsList>

                <TabsContent value="itens" className="pt-4">
                  <ItensTab preview={data} ajustes={ajustes} onAjustar={ajustarItem} />
                </TabsContent>
                <TabsContent value="cabecalho" className="pt-4">
                  <CabecalhoTab preview={data} />
                </TabsContent>
                <TabsContent value="impostos" className="pt-4">
                  <ImpostosTab preview={data} />
                </TabsContent>
                <TabsContent value="financeiro" className="pt-4">
                  <FinanceiroTab preview={data} />
                </TabsContent>
                <TabsContent value="confirmar" className="pt-4">
                  <ConfirmarTab
                    preview={data}
                    locais={locais}
                    localEstoqueId={localEstoqueId}
                    onLocalEstoqueIdChange={setLocalEstoqueId}
                    observacao={observacao}
                    onObservacaoChange={setObservacao}
                    prazoAvulso={prazoAvulso}
                    onPrazoAvulsoChange={setPrazoAvulso}
                    onConfirmar={handleConfirmar}
                    isConfirming={isConfirming}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default RecebimentoImportar;

// ── Status (pode confirmar?) ─────────────────────────────────────────────────

function StatusCard({
  preview, onConfirmar,
}: {
  preview:    RecebimentoPreview;
  onConfirmar: () => void;
}) {
  const r: PreviewResumo = preview.resumo;
  const bom = r.podeConfirmar;

  return (
    <Card className={bom ? 'border-green-500/50' : 'border-yellow-500/50'}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {bom ? (
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            ) : (
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            )}
            <div>
              <div className="font-semibold">
                {bom
                  ? 'Preview válido — pronto para confirmação'
                  : 'Existem pendências que precisam de atenção'}
              </div>
              <p className="text-sm text-muted-foreground">
                {r.itensComMatch}/{r.totalItens} itens com match ·
                {' '}{r.itensDivergentes} com divergência ·
                {' '}{r.pendenciasBloqueantes} bloqueantes
              </p>
            </div>
          </div>
          <Button onClick={onConfirmar} disabled={!bom}>
            Ir para Confirmação
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Resumo (4 cards) ─────────────────────────────────────────────────────────

function ResumoCard({ preview }: { preview: RecebimentoPreview }) {
  const { identificacao: id, totais } = preview.staging;
  const forn = preview.fornecedor;
  const po   = preview.ordemVinculada;

  const fornIcon   = forn.status === 'Encontrado'            ? <UserCheck className="h-4 w-4 text-green-500" />
                   : forn.status === 'EncontradoNaoFornecedor' ? <UserCheck className="h-4 w-4 text-yellow-500" />
                   :                                            <UserX className="h-4 w-4 text-destructive" />;

  const fornLabel  = forn.status === 'Encontrado'              ? 'Cadastrado'
                   : forn.status === 'EncontradoNaoFornecedor' ? 'Precisa marcar como fornecedor'
                   :                                             'Novo — precisa cadastrar';

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Nota</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{id.numero}/{id.serie}</div>
          <p className="text-xs text-muted-foreground mt-1">
            Emissão: {fmtData(id.dtEmissao)}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
            {fornIcon} Fornecedor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm font-semibold truncate" title={forn.nomeRazao ?? ''}>
            {forn.nomeRazao ?? '—'}
          </div>
          <p className="text-xs text-muted-foreground mt-1">{fmtCnpj(forn.cpfCnpj)}</p>
          <Badge variant={forn.status === 'Encontrado' ? 'default' : 'outline'} className="mt-1">
            {fornLabel}
          </Badge>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
            <Link2 className="h-4 w-4" /> Pedido
          </CardTitle>
        </CardHeader>
        <CardContent>
          {po ? (
            <>
              <div className="text-sm font-semibold">PO #{po.numero}</div>
              <p className="text-xs text-muted-foreground mt-1 truncate" title={po.fornecedorNome}>
                {po.fornecedorNome} · R$ {fmtMoeda(po.valorTotal)}
              </p>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold">Avulso</div>
              <p className="text-xs text-muted-foreground mt-1">Sem vínculo a pedido</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Total NF-e</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">R$ {fmtMoeda(totais.valorTotal)}</div>
          <p className="text-xs text-muted-foreground mt-1">
            Produtos: R$ {fmtMoeda(totais.valorProdutos)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Validações ───────────────────────────────────────────────────────────────

function ValidacoesCard({ erros, avisos }: { erros: Validacao[]; avisos: Validacao[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {erros.length > 0 ? (
            <AlertCircle className="h-5 w-5 text-destructive" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          )}
          Validações do XML
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {erros.map((v, i) => (
          <div key={`e${i}`} className="p-2 rounded-md bg-destructive/10 border border-destructive/50 text-sm flex gap-2">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
            <div>
              <Badge variant="destructive" className="mr-2">{v.codigo}</Badge>
              {v.mensagem}
            </div>
          </div>
        ))}
        {avisos.map((v, i) => (
          <div key={`a${i}`} className="p-2 rounded-md bg-yellow-500/10 border border-yellow-500/50 text-sm flex gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
            <div>
              <Badge variant="outline" className="mr-2">{v.codigo}</Badge>
              {v.mensagem}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Aba Itens ────────────────────────────────────────────────────────────────

function SemaforoIcon({ s }: { s: ItemPreview['semaforo'] }) {
  if (s === 'Verde')   return <PackageCheck className="h-4 w-4 text-green-500" />;
  if (s === 'Amarelo') return <Package className="h-4 w-4 text-yellow-500" />;
  return <PackageX className="h-4 w-4 text-destructive" />;
}

function ItensTab({ preview, ajustes, onAjustar }: {
  preview: RecebimentoPreview;
  ajustes: Ajustes;
  onAjustar: (numeroItem: number, parcial: AjusteItem) => void;
}) {
  const temPO = !!preview.ordemVinculada;
  // Itens em que o usuário pediu para trocar o produto casado automaticamente.
  const [trocando, setTrocando] = useState<Set<number>>(new Set());

  return (
    <div className="rounded-md border overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"></TableHead>
            <TableHead>#</TableHead>
            <TableHead>Produto (match)</TableHead>
            <TableHead>Cód. Fornec.</TableHead>
            <TableHead>Descrição XML</TableHead>
            <TableHead className="text-right">Qtd XML</TableHead>
            <TableHead className="w-24" title="Quantas unidades de estoque há em uma unidade do fornecedor (caixa com 12 → 12)">
              Fator
            </TableHead>
            <TableHead className="text-right">Qtd estoque</TableHead>
            <TableHead className="w-28">Lote</TableHead>
            <TableHead className="w-36">Validade</TableHead>
            {temPO && <TableHead className="text-right">Qtd PO disp.</TableHead>}
            <TableHead className="text-right">V. Unit.</TableHead>
            <TableHead className="text-right">V. Total</TableHead>
            <TableHead>Pendências</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {preview.itens.map((it) => {
            const n          = it.xml.numeroItem;
            const ajuste     = ajustes[n] ?? {};
            const vinculado  = Number(produtoDoItem(it, ajustes)) > 0;
            const escolhendo = it.produto.status !== 'Encontrado' || trocando.has(n) || ajuste.produtoId != null;
            const fator      = fatorDoItem(it, ajustes);
            return (
            <TableRow
              key={n}
              className={
                !vinculado                  ? 'bg-destructive/5'
              : it.semaforo === 'Amarelo'  ? 'bg-yellow-500/5'
              : ''}
            >
              <TableCell><SemaforoIcon s={it.semaforo} /></TableCell>
              <TableCell className="text-xs">{it.xml.numeroItem}</TableCell>
              <TableCell className="min-w-[260px]">
                {!escolhendo ? (
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium truncate max-w-xs" title={it.produto.descricao}>
                      {it.produto.descricao}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Cód. {it.produto.produtoId} · via {it.produto.metodoMatch} ·{' '}
                      <button type="button" className="underline"
                        onClick={() => setTrocando((s) => new Set(s).add(n))}>
                        trocar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <ProdutoSearchCombobox
                      value={ajuste.produtoId}
                      onChange={(produtoId) => onAjustar(n, { produtoId })}
                      placeholder="Vincular a um produto do cadastro..."
                    />
                    {!vinculado && (
                      <p className="text-xs text-destructive">
                        {it.produto.status === 'MultiplosCandidatos'
                          ? 'Mais de um produto parecido — escolha o correto.'
                          : 'Não encontrado no cadastro — escolha o produto. O vínculo fica salvo para as próximas notas deste fornecedor.'}
                      </p>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell className="font-mono text-xs">{it.xml.codigoProdutoFornecedor}</TableCell>
              <TableCell className="max-w-[220px] truncate text-xs" title={it.xml.descricao}>
                {it.xml.descricao}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {fmtQtd(it.xml.quantidade)} <span className="text-xs text-muted-foreground">{it.xml.unidade}</span>
              </TableCell>
              <TableCell>
                <Input
                  className="h-8 w-20 text-right" inputMode="decimal"
                  placeholder={String(it.produto.fatorConversaoEntrada ?? 1)}
                  value={ajuste.fator ?? ''}
                  onChange={(e) => onAjustar(n, { fator: e.target.value })}
                />
              </TableCell>
              <TableCell className="text-right font-medium">{fmtQtd(it.xml.quantidade * fator)}</TableCell>
              <TableCell>
                <Input
                  className="h-8 w-24" maxLength={20} value={ajuste.lote ?? ''}
                  onChange={(e) => onAjustar(n, { lote: e.target.value })}
                />
              </TableCell>
              <TableCell>
                <Input
                  className="h-8 w-36" type="date" value={ajuste.validade ?? ''}
                  onChange={(e) => onAjustar(n, { validade: e.target.value })}
                />
              </TableCell>
              {temPO && (
                <TableCell className="text-right">
                  {it.poItem
                    ? <span className="text-xs">{fmtQtd(it.poItem.quantidadeDisponivel)}</span>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
              )}
              <TableCell className="text-right">R$ {fmtMoeda(it.xml.valorUnitario)}</TableCell>
              <TableCell className="text-right font-semibold">R$ {fmtMoeda(it.xml.valorTotalBruto)}</TableCell>
              <TableCell>
                {it.pendencias.length === 0 ? (
                  <span className="text-xs text-green-600">OK</span>
                ) : (
                  <ul className="text-xs space-y-0.5">
                    {it.pendencias.map((p, i) => (
                      <li key={i}>
                        <Badge variant="outline" className="mr-1 text-[10px]">{p.tipo}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Aba Cabeçalho ────────────────────────────────────────────────────────────

function CabecalhoTab({ preview }: { preview: RecebimentoPreview }) {
  const { identificacao: ident, emitente: emit, destinatario: dest } = preview.staging;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <FileText className="h-4 w-4" /> Identificação
        </h3>
        <DL>
          <Item label="Chave" value={<span className="font-mono text-xs break-all">{ident.chaveAcesso}</span>} />
          <Item label="Natureza" value={ident.naturezaOperacao} />
          <Item label="Número/Série" value={`${ident.numero} / ${ident.serie}`} />
          <Item label="Emissão" value={fmtData(ident.dtEmissao)} />
          <Item label="Entrada/Saída" value={fmtData(ident.dtEntradaSaida)} />
          <Item
            label="Finalidade"
            value={
              { 1: 'Normal', 2: 'Complementar', 3: 'Ajuste', 4: 'Devolução' }[ident.finalidadeEmissao] ?? String(ident.finalidadeEmissao)
            }
          />
        </DL>
      </div>
      <div>
        <h3 className="font-semibold mb-2">Emitente</h3>
        <DL>
          <Item label="CNPJ/CPF" value={fmtCnpj(emit.cnpjCpf)} />
          <Item label="Razão social" value={emit.razaoSocial} />
          <Item label="IE" value={emit.inscricaoEstadual ?? '—'} />
          <Item
            label="Endereço"
            value={
              emit.endereco
                ? `${emit.endereco.logradouro}, ${emit.endereco.numero ?? 's/n'} — ${emit.endereco.bairro}, ${emit.endereco.municipio}/${emit.endereco.uf}`
                : '—'
            }
          />
        </DL>
        <Separator className="my-4" />
        <h3 className="font-semibold mb-2">Destinatário</h3>
        <DL>
          <Item label="CNPJ/CPF" value={fmtCnpj(dest.cnpjCpf)} />
          <Item label="Razão social" value={dest.razaoSocial} />
          <Item label="IE" value={dest.inscricaoEstadual ?? '—'} />
        </DL>
      </div>
    </div>
  );
}

// ── Aba Impostos ─────────────────────────────────────────────────────────────

function ImpostosTab({ preview }: { preview: RecebimentoPreview }) {
  const t = preview.staging.totais;
  const [expandido, setExpandido] = useState<number | null>(null);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="font-semibold mb-3">Totais da nota</h3>
          <DL>
            <Item label="Base ICMS"       value={`R$ ${fmtMoeda(t.baseCalculoIcms)}`} />
            <Item label="ICMS"            value={`R$ ${fmtMoeda(t.valorIcms)}`} />
            <Item label="ICMS desonerado" value={`R$ ${fmtMoeda(t.valorIcmsDesonerado)}`} />
            <Item label="Base ICMS ST"    value={`R$ ${fmtMoeda(t.baseCalculoIcmsSt)}`} />
            <Item label="ICMS ST"         value={`R$ ${fmtMoeda(t.valorIcmsSt)}`} />
            <Item label="FCP"             value={`R$ ${fmtMoeda(t.valorFcp)}`} />
            <Item label="FCP ST"          value={`R$ ${fmtMoeda(t.valorFcpSt)}`} />
            <Item label="FCP ST retido"   value={`R$ ${fmtMoeda(t.valorFcpRetidoAnterior)}`} />
            <Item label="IPI"             value={`R$ ${fmtMoeda(t.valorIpi)}`} />
            <Item label="PIS"             value={`R$ ${fmtMoeda(t.valorPis)}`} />
            <Item label="COFINS"          value={`R$ ${fmtMoeda(t.valorCofins)}`} />
            <Item label="Importação (II)" value={`R$ ${fmtMoeda(t.valorImportacao)}`} />
          </DL>
        </div>
        <div>
          <h3 className="font-semibold mb-3">Subtotais (cabeçalho do XML)</h3>
          <DL>
            <Item label="Produtos"           value={`R$ ${fmtMoeda(t.valorProdutos)}`} />
            <Item label="Frete"              value={`R$ ${fmtMoeda(t.valorFrete)}`} />
            <Item label="Seguro"             value={`R$ ${fmtMoeda(t.valorSeguro)}`} />
            <Item label="Desconto"           value={`R$ ${fmtMoeda(t.valorDesconto)}`} />
            <Item label="Outras despesas"    value={`R$ ${fmtMoeda(t.valorOutrasDespesas)}`} />
            <Item label="Aprox. tributos"    value={`R$ ${fmtMoeda(t.valorAproximadoTributos)}`} />
            <Item label="Total da NF-e"      value={<span className="font-bold text-lg">R$ {fmtMoeda(t.valorTotal)}</span>} />
          </DL>
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-3">Detalhamento por item (com rateio)</h3>
        <p className="text-xs text-muted-foreground mb-2">
          Clique em uma linha para ver impostos detalhados.
        </p>
        <div className="rounded-md border overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>#</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead className="text-right">V. Produto</TableHead>
                <TableHead className="text-right">Desc.</TableHead>
                <TableHead className="text-right">Frete</TableHead>
                <TableHead className="text-right">Seguro</TableHead>
                <TableHead className="text-right">Outros</TableHead>
                <TableHead className="text-right">Líquido</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.itens.map((it) => {
                const x = it.xml;
                const aberto = expandido === x.numeroItem;
                return (
                  <>
                    <TableRow
                      key={x.numeroItem}
                      className="cursor-pointer hover:bg-accent/50"
                      onClick={() => setExpandido(aberto ? null : x.numeroItem)}
                    >
                      <TableCell className="text-xs">{aberto ? '▾' : '▸'}</TableCell>
                      <TableCell className="text-xs">{x.numeroItem}</TableCell>
                      <TableCell className="max-w-[240px] truncate text-xs">{x.descricao}</TableCell>
                      <TableCell className="text-right">R$ {fmtMoeda(x.valorTotalBruto)}</TableCell>
                      <TableCell className="text-right text-red-600">
                        {x.descontoRateado > 0 ? `-${fmtMoeda(x.descontoRateado)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right">{x.freteRateado > 0 ? fmtMoeda(x.freteRateado) : '—'}</TableCell>
                      <TableCell className="text-right">{x.seguroRateado > 0 ? fmtMoeda(x.seguroRateado) : '—'}</TableCell>
                      <TableCell className="text-right">{x.outrasDespesasRateadas > 0 ? fmtMoeda(x.outrasDespesasRateadas) : '—'}</TableCell>
                      <TableCell className="text-right font-semibold">R$ {fmtMoeda(x.valorLiquido)}</TableCell>
                    </TableRow>
                    {aberto && (
                      <TableRow key={`${x.numeroItem}-det`} className="bg-muted/30">
                        <TableCell colSpan={9} className="p-4">
                          <DetalheImpostos item={it} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function DetalheImpostos({ item }: { item: ItemPreview }) {
  const imp = item.xml.impostos;
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
      <div>
        <div className="font-semibold mb-1">ICMS</div>
        {imp.icms ? (
          <DL>
            <Item label="CST"        value={imp.icms.cst} />
            <Item label="Origem"     value={String(imp.icms.origemMercadoria)} />
            <Item label="Base"       value={`R$ ${fmtMoeda(imp.icms.baseCalculo)}`} />
            <Item label="Alíquota"   value={`${imp.icms.aliquota.toFixed(2)}%`} />
            <Item label="Valor"      value={`R$ ${fmtMoeda(imp.icms.valor)}`} />
            {(imp.icms.valorDesonerado ?? 0) > 0 && (
              <Item label="Desonerado" value={`R$ ${fmtMoeda(imp.icms.valorDesonerado!)}`} />
            )}
          </DL>
        ) : <span className="text-muted-foreground">—</span>}
      </div>

      <div>
        <div className="font-semibold mb-1">ICMS ST / FCP</div>
        {imp.icms && (imp.icms.valorSt || imp.icms.valorFcp || imp.icms.valorFcpSt || imp.icms.valorIcmsSubstituidoAntes) ? (
          <DL>
            {imp.icms.baseCalculoSt !== undefined && <Item label="Base ST"    value={`R$ ${fmtMoeda(imp.icms.baseCalculoSt ?? 0)}`} />}
            {imp.icms.aliquotaSt    !== undefined && <Item label="Alíq. ST"   value={`${(imp.icms.aliquotaSt ?? 0).toFixed(2)}%`} />}
            {imp.icms.valorSt       !== undefined && <Item label="Valor ST"   value={`R$ ${fmtMoeda(imp.icms.valorSt ?? 0)}`} />}
            {imp.icms.percentualMva !== undefined && <Item label="MVA"        value={`${(imp.icms.percentualMva ?? 0).toFixed(2)}%`} />}
            {imp.icms.valorFcp      !== undefined && <Item label="FCP"        value={`R$ ${fmtMoeda(imp.icms.valorFcp ?? 0)}`} />}
            {imp.icms.valorFcpSt    !== undefined && <Item label="FCP ST"     value={`R$ ${fmtMoeda(imp.icms.valorFcpSt ?? 0)}`} />}
            {imp.icms.valorIcmsSubstituidoAntes !== undefined && (
              <Item label="ST retido"  value={`R$ ${fmtMoeda(imp.icms.valorIcmsSubstituidoAntes ?? 0)}`} />
            )}
          </DL>
        ) : <span className="text-muted-foreground">—</span>}
      </div>

      <div>
        <div className="font-semibold mb-1">IPI</div>
        {imp.ipi ? (
          <DL>
            <Item label="CST"      value={imp.ipi.cst} />
            <Item label="Base"     value={`R$ ${fmtMoeda(imp.ipi.baseCalculo)}`} />
            <Item label="Alíquota" value={`${imp.ipi.aliquota.toFixed(2)}%`} />
            <Item label="Valor"    value={`R$ ${fmtMoeda(imp.ipi.valor)}`} />
          </DL>
        ) : <span className="text-muted-foreground">—</span>}
      </div>

      <div>
        <div className="font-semibold mb-1">PIS / COFINS</div>
        <DL>
          {imp.pis && (
            <>
              <Item label="PIS CST"   value={imp.pis.cst} />
              <Item label="PIS base"  value={`R$ ${fmtMoeda(imp.pis.baseCalculo)}`} />
              <Item label="PIS valor" value={`R$ ${fmtMoeda(imp.pis.valor)}`} />
            </>
          )}
          {imp.cofins && (
            <>
              <Item label="COF CST"   value={imp.cofins.cst} />
              <Item label="COF base"  value={`R$ ${fmtMoeda(imp.cofins.baseCalculo)}`} />
              <Item label="COF valor" value={`R$ ${fmtMoeda(imp.cofins.valor)}`} />
            </>
          )}
        </DL>
      </div>

      {(imp.icms?.valorIcmsUfDestino || imp.icms?.valorIcmsUfRemetente) && (
        <div className="md:col-span-4">
          <div className="font-semibold mb-1">DIFAL (interestadual)</div>
          <DL>
            {imp.icms.baseCalculoUfDestino    !== undefined && <Item label="Base UF destino"    value={`R$ ${fmtMoeda(imp.icms.baseCalculoUfDestino ?? 0)}`} />}
            {imp.icms.aliquotaInterestadual   !== undefined && <Item label="Alíq. interestadual" value={`${(imp.icms.aliquotaInterestadual ?? 0).toFixed(2)}%`} />}
            {imp.icms.percentualPartilhaUfDest !== undefined && <Item label="% partilha"         value={`${(imp.icms.percentualPartilhaUfDest ?? 0).toFixed(2)}%`} />}
            {imp.icms.valorIcmsUfDestino      !== undefined && <Item label="ICMS UF destino"    value={`R$ ${fmtMoeda(imp.icms.valorIcmsUfDestino ?? 0)}`} />}
            {imp.icms.valorIcmsUfRemetente    !== undefined && <Item label="ICMS UF remetente"  value={`R$ ${fmtMoeda(imp.icms.valorIcmsUfRemetente ?? 0)}`} />}
            {imp.icms.valorFcpUfDestino       !== undefined && <Item label="FCP UF destino"     value={`R$ ${fmtMoeda(imp.icms.valorFcpUfDestino ?? 0)}`} />}
          </DL>
        </div>
      )}

      {item.xml.informacoesAdicionaisItem && (
        <div className="md:col-span-4">
          <div className="font-semibold mb-1">Informações adicionais</div>
          <p className="text-xs whitespace-pre-wrap">{item.xml.informacoesAdicionaisItem}</p>
        </div>
      )}
    </div>
  );
}

// ── Aba Financeiro ───────────────────────────────────────────────────────────

function FinanceiroTab({ preview }: { preview: RecebimentoPreview }) {
  const parcelas = preview.staging.parcelas;
  const totalNF  = preview.staging.totais.valorTotal;
  const somaParcelas = parcelas.reduce((acc, p) => acc + p.valor, 0);
  const diff = Math.abs(somaParcelas - totalNF);
  const bate = diff <= 0.02 && parcelas.length > 0;

  if (parcelas.length === 0)
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Esta NF-e não contém parcelas (&lt;cobr&gt;).</p>
        <p className="text-xs text-muted-foreground">
          Na gravação, o sistema gerará uma única parcela com vencimento baseado no prazo de pagamento da PO ou, em recebimento avulso, será solicitado.
        </p>
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-xs text-muted-foreground">Total da NF-e</div>
            <div className="text-lg font-semibold">R$ {fmtMoeda(totalNF)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-xs text-muted-foreground">Soma das parcelas</div>
            <div className="text-lg font-semibold">R$ {fmtMoeda(somaParcelas)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {parcelas.length} parcela{parcelas.length > 1 ? 's' : ''}
            </div>
          </CardContent>
        </Card>
        <Card className={bate ? 'border-green-500/50' : 'border-destructive/50'}>
          <CardContent className="pt-6">
            <div className="text-xs text-muted-foreground">Consistência</div>
            <div className={`text-lg font-semibold ${bate ? 'text-green-600' : 'text-destructive'}`}>
              {bate ? 'OK — soma confere' : `Diferença: R$ ${fmtMoeda(diff)}`}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-md border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Parcela</TableHead>
              <TableHead>Vencimento</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">% do total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parcelas.map((p) => (
              <TableRow key={p.numero}>
                <TableCell className="font-mono">{p.numero}</TableCell>
                <TableCell>{fmtData(p.dataVencimento)}</TableCell>
                <TableCell className="text-right">R$ {fmtMoeda(p.valor)}</TableCell>
                <TableCell className="text-right text-xs text-muted-foreground">
                  {totalNF > 0 ? `${((p.valor / totalNF) * 100).toFixed(2)}%` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Aba Confirmar ────────────────────────────────────────────────────────────

function ConfirmarTab({
  preview,
  locais,
  localEstoqueId,
  onLocalEstoqueIdChange,
  observacao,
  onObservacaoChange,
  prazoAvulso,
  onPrazoAvulsoChange,
  onConfirmar,
  isConfirming,
}: {
  preview:                 RecebimentoPreview;
  locais:                  { id: number; descricao: string }[];
  localEstoqueId:          string;
  onLocalEstoqueIdChange:  (v: string) => void;
  observacao:              string;
  onObservacaoChange:      (v: string) => void;
  prazoAvulso:             string;
  onPrazoAvulsoChange:     (v: string) => void;
  onConfirmar:             () => void;
  isConfirming:            boolean;
}) {
  const r = preview.resumo;
  const stg = preview.staging;
  const parcelasOk = stg.parcelas.length > 0
    ? Math.abs(stg.parcelas.reduce((a, p) => a + p.valor, 0) - stg.totais.valorTotal) <= 0.02
    : true;

  const checklist: Array<{ ok: boolean; label: string }> = [
    { ok: !stg.validacoes.some(v => v.severidade === 'Erro'), label: 'XML sem erros estruturais' },
    { ok: preview.fornecedor.fornecedorId != null && preview.fornecedor.fornecedorId > 0,
      label: 'Fornecedor cadastrado no banco' },
    { ok: r.itensSemMatch === 0,                               label: 'Todos os itens com produto vinculado' },
    { ok: r.pendenciasBloqueantes === 0,                       label: 'Nenhuma pendência bloqueante' },
    { ok: !!localEstoqueId,                                    label: 'Local de estoque de destino selecionado' },
    { ok: parcelasOk,                                          label: stg.parcelas.length > 0
                                                                    ? 'Parcelas somam o total da NF-e'
                                                                    : 'Prazo informado (nota sem parcelas no XML)' },
  ];

  const tudoOk = checklist.every(c => c.ok);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2 space-y-4">
        <h3 className="font-semibold">Configuração do recebimento</h3>

        <div className="space-y-1">
          <Label>Local de estoque de destino *</Label>
          <Select value={localEstoqueId} onValueChange={onLocalEstoqueIdChange}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione um local…" />
            </SelectTrigger>
            <SelectContent>
              {locais.map(l => (
                <SelectItem key={l.id} value={String(l.id)}>
                  {l.descricao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Todos os itens entrarão neste local. A iteração 5 permitirá escolher por item.
          </p>
        </div>

        {stg.parcelas.length === 0 && (
          <div className="space-y-1">
            <Label>Prazo de pagamento (dias) — nota sem parcelas no XML</Label>
            <Input
              type="number"
              value={prazoAvulso}
              onChange={(e) => onPrazoAvulsoChange(e.target.value)}
              placeholder="30"
            />
            <p className="text-xs text-muted-foreground">
              Uma parcela única será gerada com vencimento = data de emissão + N dias.
            </p>
          </div>
        )}

        <div className="space-y-1">
          <Label>Observação (opcional)</Label>
          <Textarea
            value={observacao}
            onChange={(e) => onObservacaoChange(e.target.value)}
            rows={3}
            placeholder="Ex: conferência feita pelo João"
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold">Checklist de confirmação</h3>
        <ul className="space-y-2">
          {checklist.map((c, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              {c.ok ? (
                <Check className="h-4 w-4 text-green-600 mt-0.5" />
              ) : (
                <X className="h-4 w-4 text-destructive mt-0.5" />
              )}
              <span className={c.ok ? '' : 'text-muted-foreground'}>{c.label}</span>
            </li>
          ))}
        </ul>

        <Separator />

        <div className="text-sm space-y-1">
          <div className="text-muted-foreground">Serão criados:</div>
          <ul className="text-xs list-disc pl-5 space-y-0.5">
            <li>1 recebimento (compra_recebimento) + {preview.itens.length} itens</li>
            <li>1 movimento de estoque (entrada) + {preview.itens.length} linhas</li>
            <li>1 conta a pagar + {stg.parcelas.length || 1} parcela{(stg.parcelas.length || 1) > 1 ? 's' : ''}</li>
            <li>Atualização do custo em ProdutoFornecedor por item</li>
            <li>Registro no histórico de preço de compra</li>
            {preview.ordemVinculada && <li>Atualização da PO #{preview.ordemVinculada.numero}</li>}
          </ul>
        </div>

        <Button
          onClick={onConfirmar}
          disabled={!tudoOk || isConfirming}
          className="w-full gap-2"
        >
          <CheckCircle2 className="h-4 w-4" />
          {isConfirming ? 'Confirmando…' : 'Confirmar Recebimento'}
        </Button>
      </div>
    </div>
  );
}

// ── Definition list ──────────────────────────────────────────────────────────

function DL({ children }: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">{children}</dl>;
}

function Item({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium break-words">{value}</dd>
    </>
  );
}
